<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { RecoveryMethodKey } from './methods'
import { useRecoveryConfig } from './useRecoveryConfig'
import useWebapp from '@/composables/webapp'

const { t, locale } = useI18n()
const { user } = useLocalStorage()
const { onlineUrl } = useRecoveryConfig()
const { getWebapp } = useWebapp()

const props = defineProps<{
  isOpen: boolean
  methodKey: RecoveryMethodKey | null
}>()

const emit = defineEmits<{ (e: 'close'): void }>()

const label = computed(() =>
  props.methodKey ? t(`pages.loyaltyPage.tabs.recovery.methods.${props.methodKey}.label`) : ''
)
const description = computed(() =>
  props.methodKey ? t(`pages.loyaltyPage.tabs.recovery.methods.${props.methodKey}.description`) : ''
)

const webappColor = ref('')
const webappLink = ref('')
const hasFetchedWebapp = ref(import.meta.env.VITE_ENV === 'local')

const withHash = (color: string) => (color.startsWith('#') ? color : `#${color}`)

const previewBase = computed(() => {
  const { slug } = user.value.merchant

  if (import.meta.env.VITE_ENV === 'local') return `http://localhost:3004/${slug}`
  if (!webappLink.value) return ''

  try {
    const url = new URL(`https://${webappLink.value.replace(/^https?:\/\//, '')}`)
    const [merchantSegment] = url.pathname.split('/').filter(Boolean)

    return `${url.origin}/${merchantSegment || slug}`
  } catch {
    return ''
  }
})

const previewUrl = computed(() => {
  if (!props.methodKey || !previewBase.value) return ''

  const merchant = user.value.merchant
  const url = new URL(`${previewBase.value}/preview/reward-retrieval`)

  url.searchParams.set('method', props.methodKey)
  url.searchParams.set('hash', 'preview')
  url.searchParams.set('locale', locale.value)
  url.searchParams.set('rewardName', t('pages.loyaltyPage.tabs.recovery.experienceModal.sampleReward'))

  if (webappColor.value) url.searchParams.set('color', withHash(webappColor.value))
  if (merchant.media.logo) url.searchParams.set('logo', merchant.media.logo)
  if (merchant.name) url.searchParams.set('storeName', merchant.name)
  if (props.methodKey === 'online' && onlineUrl.value) url.searchParams.set('onlineOrderUrl', onlineUrl.value)

  return url.toString()
})

watch(
  () => props.isOpen,
  async (isOpen) => {
    if (!isOpen || webappLink.value) return

    const webapp = await getWebapp()
    if (webapp?.color) webappColor.value = webapp.color
    if (webapp?.link) webappLink.value = webapp.link

    hasFetchedWebapp.value = true
  }
)
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :heading-title="t('pages.loyaltyPage.tabs.recovery.experienceModal.title')"
    size="lg"
    @close="emit('close')"
  >
    <div v-if="methodKey" class="flex flex-col items-center gap-5 py-2">
      <BasePhone size="md">
        <div class="relative size-full bg-muted">
          <iframe
            v-if="isOpen && previewUrl"
            :key="previewUrl"
            :src="previewUrl"
            :title="t('pages.loyaltyPage.tabs.recovery.experienceModal.previewAlt', { method: label })"
            class="size-full border-0"
          />
          <div v-else-if="hasFetchedWebapp" class="absolute inset-0 flex items-center justify-center px-6 text-center">
            <span class="text-xs text-muted-foreground">
              {{ t('pages.loyaltyPage.tabs.recovery.experienceModal.unavailable') }}
            </span>
          </div>
        </div>
      </BasePhone>

      <div class="max-w-xs text-center">
        <p class="text-sm font-medium text-foreground">{{ label }}</p>
        <p class="mt-1 text-sm text-muted-foreground">{{ description }}</p>
      </div>
    </div>
  </BaseModal>
</template>
